import React, { useMemo, useState } from "react";
import { formatMiles } from "../../../../utils/miles";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { FiArrowUpRight } from "react-icons/fi";

const getAnticipoId = (row) => row?.idanticipo || row?.idAnticipo || row?.id;
const getAnticipoCierre = (row) => row?.idcierre || row?.idLiquidacion || row?.idLiquidacionClientes;
const getAnticipoPaciente = (row) => row?.paciente || row?.NombrePaciente || row?.nombrepaciente || "Anticipo";
const getAnticipoFactura = (row) => row?.factura || row?.recibo || row?.comprobante || "-";
const getMonto = (row) => Number(row?.monto || row?.Monto || 0);
const formatSignedMiles = (value) => {
  const amount = Number(value) || 0;
  if (!amount) return "-";
  return `${amount < 0 ? "-" : ""}${formatMiles(Math.abs(amount))}`;
};

function LiquidacionesAsociadas({
  liquidaciones = [],
  liqDetalles = {},
  proveedores = [],
  anticipos = [],
  loadingDetalles,
  orden,
}) {
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingXlsx, setGeneratingXlsx] = useState(false);

  const anticiposByCierre = useMemo(() => {
    const map = {};
    for (const ant of anticipos) {
      const id = getAnticipoCierre(ant);
      if (!id) continue;
      map[id] = (map[id] || 0) + getMonto(ant);
    }
    return map;
  }, [anticipos]);

  const anticiposSinCierre = useMemo(
    () => anticipos.filter((ant) => !getAnticipoCierre(ant)),
    [anticipos]
  );

  const totals = useMemo(() => {
    let honorarios = 0;
    let tarifa = 0;
    for (const liq of liquidaciones) {
      const det = liqDetalles[liq.idLiquidacionClientes];
      if (det) {
        if (det.honorarios !== undefined) honorarios += Number(det.honorarios) || 0;
        if (det.tarifa !== undefined) tarifa += Number(det.tarifa) || 0;
      }
    }
    for (const p of proveedores) {
      if (p.monto !== undefined) honorarios += Number(p.monto) || 0;
    }
    const totalAnticiposConCierre = anticipos.reduce(
      (acc, ant) => acc + (getAnticipoCierre(ant) ? getMonto(ant) : 0),
      0
    );
    const totalAnticiposSinCierre = anticipos.reduce(
      (acc, ant) => acc + (!getAnticipoCierre(ant) ? getMonto(ant) : 0),
      0
    );
    tarifa -= totalAnticiposConCierre;
    return {
      honorarios,
      tarifa,
      anticipos: totalAnticiposSinCierre - totalAnticiposConCierre,
      count: liquidaciones.length + proveedores.length + anticipos.length,
    };
  }, [liquidaciones, liqDetalles, proveedores, anticipos]);

  const nothingToExport = !liquidaciones.length && !proveedores.length && !anticipos.length;

  const handleDownloadPdf = () => {
    if (nothingToExport) return;
    generatePdf({ liquidaciones, liqDetalles, proveedores, anticipos, setGeneratingPdf, orden });
  };

  const handleDownloadXlsx = () => {
    if (nothingToExport) return;
    generateXlsx({ liquidaciones, liqDetalles, proveedores, anticipos, setGeneratingXlsx, orden, totals });
  };

  const tableStyle = {
    borderCollapse: "collapse",
    width: "100%",
    opacity: loadingDetalles ? 0.5 : 1,
    fontSize: 14,
    tableLayout: "fixed",
    minWidth: 980,
  };

  const thStyle = {
    id: baseTh("7%"),
    paciente: baseTh("27%"),
    inicio: baseTh("10%"),
    fin: baseTh("10%"),
    factura: baseTh("11%"),
    tarifa: baseTh("10%", "right"),
    honorarios: baseTh("10%", "right"),
    anticipos: baseTh("10%", "right"),
    estado: baseTh("8%"),
  };

  return (
    <div style={{ position: "relative" }}>
      <div style={headerBarStyle}>
        <h2 style={{ margin: 0, color: "#1e40af" }}>Liquidaciones Asociadas</h2>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <ResumenCompacto totals={totals} loading={loadingDetalles} />

          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={generatingPdf || nothingToExport || loadingDetalles}
            style={{
              ...pdfButtonStyle,
              opacity: generatingPdf || nothingToExport ? 0.6 : 1,
              cursor: generatingPdf || nothingToExport ? "not-allowed" : "pointer",
            }}
          >
            {generatingPdf ? <><span style={miniSpinnerStyle} /> Generando...</> : <>PDF</>}
          </button>

          <button
            type="button"
            onClick={handleDownloadXlsx}
            disabled={generatingXlsx || nothingToExport || loadingDetalles}
            style={{
              ...xlsxButtonStyle,
              opacity: generatingXlsx || nothingToExport ? 0.6 : 1,
              cursor: generatingXlsx || nothingToExport ? "not-allowed" : "pointer",
            }}
          >
            {generatingXlsx ? <><span style={miniSpinnerGreenStyle} /> Generando...</> : <>Excel</>}
          </button>
        </div>
      </div>

      {nothingToExport ? (
        <p style={{ marginTop: 12 }}>No hay liquidaciones, proveedores ni anticipos para esta orden.</p>
      ) : (
        <div style={{ position: "relative", overflowX: "auto" }}>
          {loadingDetalles && (
            <div style={overlayStyle}>
              <div style={bigSpinnerStyle} />
              <span style={{ marginLeft: 10, color: "#333", fontSize: 14 }}>
                Cargando liquidaciones...
              </span>
            </div>
          )}

          <table style={tableStyle}>
            <thead style={{ background: "#f1f5f9" }}>
              <tr>
                <th style={thStyle.id}>ID</th>
                <th style={thStyle.paciente}>Usuario</th>
                <th style={thStyle.inicio}>Inicio</th>
                <th style={thStyle.fin}>Fin</th>
                <th style={thStyle.factura}>Factura N</th>
                <th style={thStyle.tarifa}>Tarifa</th>
                <th style={thStyle.honorarios}>Honorarios</th>
                <th style={thStyle.anticipos}>Anticipos</th>
                <th style={thStyle.estado}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {liquidaciones.map((liq, idx) => {
                const det = liqDetalles[liq.idLiquidacionClientes] || {};
                const totalAnticipo = anticiposByCierre[liq.idLiquidacionClientes] || 0;
                const tarifaNeta =
                  det.tarifa !== undefined ? (Number(det.tarifa) || 0) - totalAnticipo : undefined;
                return (
                  <tr
                    key={`liq-${liq.idLiquidacionClientes}`}
                    style={{ background: idx % 2 === 0 ? "#ffffff" : "#f8fafc" }}
                  >
                    <td style={tdBase}>{liq.idLiquidacionClientes}</td>
                    <td style={tdBase}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <a
                          href={`/fichaliquidacion/${liq.idLiquidacionClientes}`}
                          style={{ color: "#1d4ed8", textDecoration: "none", fontWeight: 600, wordBreak: "break-word" }}
                        >
                          {liq.NombrePaciente
                            ? `${liq.NombrePaciente} #${liq.idLiquidacionClientes}`
                            : `PacienteID:${liq.IdPaciente} #${liq.idLiquidacionClientes}`}
                        </a>
                        <a
                          href={`/fichaliquidacion/${liq.idLiquidacionClientes}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Abrir liquidación ${liq.idLiquidacionClientes} en otra ventana`}
                          title="Abrir liquidación en otra ventana"
                          style={openLiquidacionStyle}
                        >
                          <FiArrowUpRight aria-hidden="true" />
                        </a>
                      </div>
                    </td>
                    <td style={tdBase}>{liq.fechainicio || "-"}</td>
                    <td style={tdBase}>{liq.fechafin || "-"}</td>
                    <td style={tdBase}>{liq.facturanumero?.trim() || "-"}</td>
                    <td style={{ ...tdBase, textAlign: "right" }}>
                      {tarifaNeta !== undefined ? formatMiles(tarifaNeta) : "..."}
                    </td>
                    <td style={{ ...tdBase, textAlign: "right" }}>
                      {det.honorarios !== undefined ? formatMiles(det.honorarios) : "..."}
                    </td>
                    <td style={{ ...tdBase, textAlign: "right", color: totalAnticipo ? "#dc2626" : "#1f2937" }}>
                      {totalAnticipo ? `-${formatMiles(totalAnticipo)}` : "-"}
                    </td>
                    <td style={tdBase}>{liq.estado || "-"}</td>
                  </tr>
                );
              })}

              {proveedores.map((prov, idx) => (
                <tr
                  key={`prov-${prov.idpagoproveedor}`}
                  style={{ background: idx % 2 === 0 ? "#ecfdf5" : "#d1fae5" }}
                >
                  <td style={tdBase}>{prov.idpagoproveedor}</td>
                  <td style={tdBase}>
                    <span style={{ color: "#047857", fontWeight: 600 }}>
                      {prov.proveedor} #{prov.idpagoproveedor}
                    </span>
                  </td>
                  <td style={tdBase}>{prov.fechaorden || "-"}</td>
                  <td style={tdBase}>-</td>
                  <td style={tdBase}>{prov.factura?.trim() || "-"}</td>
                  <td style={{ ...tdBase, textAlign: "right" }}>-</td>
                  <td style={{ ...tdBase, textAlign: "right" }}>
                    {prov.monto !== undefined ? formatMiles(prov.monto) : "..."}
                  </td>
                  <td style={{ ...tdBase, textAlign: "right" }}>-</td>
                  <td style={tdBase}>{prov.estado || "-"}</td>
                </tr>
              ))}

              {anticiposSinCierre.map((ant, idx) => (
                <tr
                  key={`ant-${getAnticipoId(ant) || idx}`}
                  style={{ background: idx % 2 === 0 ? "#fee2e2" : "#fecaca" }}
                >
                  <td style={tdBase}>{getAnticipoId(ant)}</td>
                  <td style={tdBase}>
                    <span style={{ color: "#b91c1c", fontWeight: 600 }}>
                      {getAnticipoPaciente(ant)} #{getAnticipoId(ant)}
                    </span>
                  </td>
                  <td style={tdBase}>{ant.fecha || "-"}</td>
                  <td style={tdBase}>-</td>
                  <td style={tdBase}>{getAnticipoFactura(ant)}</td>
                  <td style={{ ...tdBase, textAlign: "right" }}>-</td>
                  <td style={{ ...tdBase, textAlign: "right" }}>-</td>
                  <td style={{ ...tdBase, textAlign: "right", color: "#b91c1c", fontWeight: 600 }}>
                    {formatMiles(getMonto(ant))}
                  </td>
                  <td style={tdBase}>ANTICIPO</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#eff6ff", fontWeight: 600, borderTop: "2px solid #2563eb" }}>
                <td colSpan={5} style={{ padding: "8px 10px", textAlign: "right" }}>Totales:</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>{formatMiles(totals.tarifa)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>{formatMiles(totals.honorarios)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", color: "#dc2626" }}>
                  {formatSignedMiles(totals.anticipos)}
                </td>
                <td>-</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

const baseTh = (width, align = "left") => ({
  width,
  padding: "6px 10px",
  textAlign: align,
  borderBottom: "1px solid #e5e7eb",
  fontSize: 13,
  fontWeight: 600,
  color: "#374151",
});

const tdBase = {
  padding: "6px 10px",
  borderBottom: "1px solid #f1f5f9",
  fontSize: 13,
  color: "#1f2937",
  verticalAlign: "middle",
};

const openLiquidacionStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
  width: 26,
  height: 26,
  border: "1px solid #bfdbfe",
  borderRadius: 6,
  background: "#eff6ff",
  color: "#1d4ed8",
  fontSize: 17,
  textDecoration: "none",
};

const headerBarStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 14,
  marginBottom: 14,
};

const pdfButtonStyle = {
  padding: "8px 14px",
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: 6,
  boxShadow: "0 1px 2px rgba(0,0,0,.08)",
};

const xlsxButtonStyle = {
  ...pdfButtonStyle,
  background: "#16a34a",
};

const miniSpinnerStyle = {
  width: 14,
  height: 14,
  border: "2px solid rgba(255,255,255,.35)",
  borderTop: "2px solid #fff",
  borderRight: "2px solid #fff",
  borderRadius: "50%",
  animation: "spin .8s linear infinite",
};

const miniSpinnerGreenStyle = { ...miniSpinnerStyle };

const bigSpinnerStyle = {
  width: 32,
  height: 32,
  border: "3px solid #ddd",
  borderTop: "3px solid #2563eb",
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
};

const overlayStyle = {
  position: "absolute",
  inset: 0,
  background: "rgba(255,255,255,0.65)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 5,
};

function ResumenCompacto({ totals, loading }) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        fontSize: 12,
        background: "#eff6ff",
        border: "1px solid #2563eb",
        padding: "6px 10px",
        borderRadius: 8,
        lineHeight: 1.3,
        color: "#1e40af",
      }}
    >
      <span>Registros: <strong>{totals.count}</strong></span>
      <span>Honorarios: <strong>{loading ? "..." : formatMiles(totals.honorarios)}</strong></span>
      <span>Anticipos: <strong>{loading ? "..." : formatSignedMiles(totals.anticipos)}</strong></span>
      <span>Tarifa: <strong>{loading ? "..." : formatMiles(totals.tarifa)}</strong></span>
    </div>
  );
}

function buildExportRows({ liquidaciones, liqDetalles, proveedores, anticipos }) {
  const byCierre = anticipos.reduce((acc, ant) => {
    const id = getAnticipoCierre(ant);
    if (id) acc[id] = (acc[id] || 0) + getMonto(ant);
    return acc;
  }, {});

  const liqRows = liquidaciones.map((liq) => {
    const det = liqDetalles[liq.idLiquidacionClientes] || {};
    const anticipo = byCierre[liq.idLiquidacionClientes] || 0;
    const tarifaNeta = det.tarifa !== undefined ? (Number(det.tarifa) || 0) - anticipo : "";
    return [
      liq.idLiquidacionClientes,
      liq.NombrePaciente
        ? `${liq.NombrePaciente} #${liq.idLiquidacionClientes}`
        : `PacienteID:${liq.IdPaciente} #${liq.idLiquidacionClientes}`,
      liq.fechainicio || "-",
      liq.fechafin || "-",
      liq.facturanumero?.trim() || "-",
      tarifaNeta,
      det.honorarios !== undefined ? Number(det.honorarios) || 0 : "",
      anticipo ? -anticipo : "",
      liq.estado || "-",
    ];
  });

  const provRows = proveedores.map((prov) => [
    prov.idpagoproveedor,
    `${prov.proveedor} #${prov.idpagoproveedor}`,
    prov.fechaorden || "-",
    "-",
    prov.factura?.trim() || "-",
    "",
    prov.monto !== undefined ? Number(prov.monto) || 0 : "",
    "",
    prov.estado || "-",
  ]);

  const antRows = anticipos
    .filter((ant) => !getAnticipoCierre(ant))
    .map((ant) => [
      getAnticipoId(ant),
      `${getAnticipoPaciente(ant)} #${getAnticipoId(ant)}`,
      ant.fecha || "-",
      "-",
      getAnticipoFactura(ant),
      "",
      "",
      getMonto(ant),
      "ANTICIPO",
    ]);

  return [...liqRows, ...provRows, ...antRows];
}

function generateXlsx({ liquidaciones, liqDetalles, proveedores, anticipos, setGeneratingXlsx, orden, totals }) {
  setGeneratingXlsx(true);
  try {
    const wb = XLSX.utils.book_new();
    const headers = ["ID", "Usuario", "Inicio", "Fin", "Factura N", "Tarifa", "Honorarios", "Anticipos", "Estado"];
    const rows = buildExportRows({ liquidaciones, liqDetalles, proveedores, anticipos });
    const totalRow = ["", "TOTALES", "", "", "", totals.tarifa, totals.honorarios, totals.anticipos, ""];

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows, totalRow]);
    ws["!cols"] = [
      { wch: 10 },
      { wch: 40 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 14 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Liquidaciones");
    const safeInicio = (orden?.fechainicio || "").replace(/[/\s:]/g, "-");
    const safeFin = (orden?.fechafin || "").replace(/[/\s:]/g, "-");
    XLSX.writeFile(wb, `Resumen_ORDEN_ID_${orden?.idordendepago || "NA"}_${safeInicio}_${safeFin}.xlsx`);
  } catch (e) {
    console.error("Error generando Excel:", e);
    alert("Error generando el Excel.");
  } finally {
    setGeneratingXlsx(false);
  }
}

function generatePdf({ liquidaciones, liqDetalles, proveedores, anticipos, setGeneratingPdf, orden }) {
  setGeneratingPdf(true);
  try {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4", compress: true });
    const marginX = 36;
    let cursorY = 40;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(30);
    doc.text("Liquidaciones Asociadas", marginX, cursorY);
    cursorY += 16;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Generado: ${new Date().toLocaleString()}`, marginX, cursorY);
    cursorY += 10;

    const body = buildExportRows({ liquidaciones, liqDetalles, proveedores, anticipos }).map((row) => [
      String(row[0] ?? ""),
      String(row[1] ?? ""),
      String(row[2] ?? ""),
      String(row[3] ?? ""),
      String(row[4] ?? ""),
      row[5] === "" ? "-" : formatMiles(row[5]),
      row[6] === "" ? "-" : formatMiles(row[6]),
      row[7] === "" ? "-" : formatMiles(row[7]),
      String(row[8] ?? ""),
    ]);

    const totalHonorarios =
      liquidaciones.reduce((acc, liq) => {
        const det = liqDetalles[liq.idLiquidacionClientes];
        return acc + (det?.honorarios !== undefined ? Number(det.honorarios) || 0 : 0);
      }, 0) +
      proveedores.reduce((acc, prov) => acc + (Number(prov.monto) || 0), 0);

    const totalTarifa =
      liquidaciones.reduce((acc, liq) => {
        const det = liqDetalles[liq.idLiquidacionClientes];
        return acc + (det?.tarifa !== undefined ? Number(det.tarifa) || 0 : 0);
      }, 0) -
      anticipos.reduce((acc, ant) => acc + (getAnticipoCierre(ant) ? getMonto(ant) : 0), 0);

    const totalAnticipos = anticipos.reduce(
      (acc, ant) => acc + (getAnticipoCierre(ant) ? -getMonto(ant) : getMonto(ant)),
      0
    );

    doc.autoTable({
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      head: [["ID", "Usuario", "Inicio", "Fin", "Factura N", "Tarifa", "Honorarios", "Anticipos", "Estado"]],
      body,
      styles: { fontSize: 8, cellPadding: 4, lineWidth: 0.1, overflow: "linebreak" },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 9, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 190 },
        2: { cellWidth: 65 },
        3: { cellWidth: 65 },
        4: { cellWidth: 70 },
        5: { cellWidth: 75, halign: "right" },
        6: { cellWidth: 75, halign: "right" },
        7: { cellWidth: 75, halign: "right" },
        8: { cellWidth: 65 },
      },
      theme: "grid",
      didDrawPage: () => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(7);
        doc.setTextColor(120);
        doc.text(`Pagina ${doc.internal.getNumberOfPages()}`, pageWidth - marginX - 50, pageHeight - 12);
      },
    });

    let afterY = doc.lastAutoTable.finalY + 12;
    if (afterY > doc.internal.pageSize.getHeight() - 80) {
      doc.addPage();
      afterY = 40;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(30);
    doc.text("Totales", marginX, afterY);
    afterY += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    [
      `Registros: ${liquidaciones.length + proveedores.length + anticipos.length}`,
      `Honorarios: ${formatMiles(totalHonorarios)}`,
      `Anticipos: ${formatSignedMiles(totalAnticipos)}`,
      `Tarifa: ${formatMiles(totalTarifa)}`,
    ].forEach((line, index) => doc.text(line, marginX + 8, afterY + index * 12));

    const safeInicio = (orden?.fechainicio || "").replace(/[/\s:]/g, "-");
    const safeFin = (orden?.fechafin || "").replace(/[/\s:]/g, "-");
    doc.save(`Resumen_ORDEN_ID_${orden?.idordendepago || "NA"}_${safeInicio}_${safeFin}.pdf`);
  } catch (e) {
    console.error("Error generando PDF liquidaciones:", e);
    alert("Error generando el PDF.");
  } finally {
    setGeneratingPdf(false);
  }
}

export default LiquidacionesAsociadas;
